import React, { useRef } from 'react';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Typography, Box, useMediaQuery, useTheme } from '@mui/material';
import { Grid } from "@mui/system";
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import SchoolIcon from '@mui/icons-material/School';
import { motion, useInView } from "framer-motion";

const TimelineItemWithAnimation = ({ item, icon, isLast }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <TimelineItem
      sx={{
        "&::before": {
          content: '""',
          width: isSmallScreen ? "0px" : "unset",
          position: isSmallScreen ? "absolute" : "unset",
          left: isSmallScreen ? "calc(50% - 1px)" : "unset",
        },
      }}
    >
      <TimelineSeparator>
        <TimelineDot sx={{ backgroundColor: "#305041", padding: "8px" }}>
          {icon}
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 90 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Typography variant='H24px' color='textPrimary'>{item.title}</Typography>
          <Typography variant="body1" color="#8d8d8d" sx={{ my: 0.5 }}>
            {item.company || item.institution} / {item.period}
          </Typography>
          <Box color="textSecondary">{item.description}</Box>
        </motion.div>
      </TimelineContent>
    </TimelineItem>
  );
};

const WorkExperience = ({ currentLang = "tr" }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Gelen dili normalize et
  const normalizedLang = String(currentLang).toLowerCase();

  // Dil bazlı dinamik veri havuzu
  const translations = {
    tr: {
      sectionTitle: "Deneyim",
      workCategory: "İŞ DENEYİMİ",
      eduCategory: "EĞİTİM",
      current: "Devam Ediyor",
      selfDevTitle: "Front-End & Full Stack Geliştirici",
      selfDevCompany: "Kişisel Gelişim & Freelance",
      selfDevDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Frontend ve backend geliştirme becerilerimi artırmak için zaman ayırdım.</li>
          <li>• Gerçek dünya kullanımına ve yayına hazır uygulamalara odaklanarak modern web teknolojileri (React, React Native, Node.js, Express, MongoDB vb.) ile birden fazla kişisel proje geliştirdim ve yayına aldım.</li>
        </ul>
      ),
      pokaTitle: "Frontend Geliştirici",
      pokaDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• React, Next.js ve Python kullanarak web uygulamaları geliştirme ve iyileştirme.</li>
          <li>• Gerektiğinde Python ile arka yüz (backend) geliştirmeye katkı sağlama.</li>
          <li>• Tasarımcılarla iş birliği yaparak Figma üzerindeki yeni tasarımları uygulamaya entegre etme.</li>
          <li>• Material UI kullanarak tasarımları uygulamaya başarılı bir şekilde dökme.</li>
          <li>• Arka yüz geliştiricileriyle koordineli ve uyumlu bir şekilde çalışma.</li>
        </ul>
      ),
      osfTitle: "Full Stack Geliştirici Stajyeri",
      osfDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• HTML, CSS, JavaScript ve Node.js teknolojilerini kullanarak web uygulamaları geliştirme ve süreçleri iyileştirme.</li>
        </ul>
      ),
      cukurovaTitle: "Lisans Derecesi, Bilgisayar Mühendisliği",
      cukurovaInstitution: "Çukurova Üniversitesi",
      cukurovaDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Bir yıllık hazırlık sınıfının ardından, yazılım geliştirmenin temellerinin atıldığı bölüme geçiş yaptım.</li>
        </ul>
      ),
      aveiroTitle: "Lisans Derecesi, Bilgisayar ve Bilişim Mühendisliği",
      aveiroInstitution: "Aveiro Üniversitesi",
      aveiroDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Portekiz'deki Aveiro Üniversitesi'nde geçirdiğim Erasmus programı süresince hem eğitimimi hem de İngilizce dil becerilerimi geliştirerek sosyal açıdan büyük kazanımlar elde ettim.</li>
        </ul>
      )
    },
    en: {
      sectionTitle: "Experience",
      workCategory: "WORK EXPERIENCE",
      eduCategory: "EDUCATION",
      current: "Current",
      selfDevTitle: "Frontend & Full Stack Developer",
      selfDevCompany: "Self-Development",
      selfDevDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Dedicated time to improving my skills in frontend and backend development.</li>
          <li>• Built and deployed multiple personal projects using modern web technologies (React, React Native, Node.js, Express, MongoDB, etc.), focusing on real-world usability and production-ready applications.</li>
        </ul>
      ),
      pokaTitle: "Frontend Developer",
      pokaDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Developing and improving web application using React, Next.js and Python.</li>
          <li>• Backend development with Python when needed.</li>
          <li>• Integrating new designs from Figma to application by collaborating with designers.</li>
          <li>• Integrating the design into the application using Material UI.</li>
          <li>• Working in collaboration with back end developers.</li>
        </ul>
      ),
      osfTitle: "Full Stack Developer Intern",
      osfDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• Developing and improving web applications using HTML, CSS, Javascript and Node.js technologies.</li>
        </ul>
      ),
      cukurovaTitle: "Bachelors Degree, Computer Engineering",
      cukurovaInstitution: "Cukurova University",
      cukurovaDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• After one year of preparatory class, I transitioned to the department, where the foundations of software development were laid.</li>
        </ul>
      ),
      aveiroTitle: "Bachelors Degree, Computer and Informatics Engineering",
      aveiroInstitution: "Aveiro University",
      aveiroDesc: (
        <ul style={{ listStyleType: "none", paddingLeft: "10px" }}>
          <li>• During my Erasmus program at Aveiro University in Portugal, I improved both my education and English skills, which greatly helped me socially.</li>
        </ul>
      )
    }
  };

  const t = translations[normalizedLang] || translations.tr;

  const experiences = [
    {
      category: t.workCategory,
      items: [
        {
          title: t.selfDevTitle,
          company: t.selfDevCompany,
          period: `11.2025 - ${t.current}`,
          description: t.selfDevDesc,
        }, 
        {
          title: t.pokaTitle,
          company: 'PokaMind / Remote - Stockholm',
          period: '07.2024 - 10.2025',
          description: t.pokaDesc,
        },        
        {
          title: t.osfTitle,
          company: 'OSF Digital / Remote - İzmir',
          period: '07.2022 - 09.2022',
          description: t.osfDesc,
        },
      ],
    },
    {
      category: t.eduCategory,
      items: [
        {
          title: t.cukurovaTitle,
          institution: t.cukurovaInstitution,
          period: '2017 - 2023',
          description: t.cukurovaDesc,
        },
        {
          title: t.aveiroTitle,
          institution: t.aveiroInstitution,
          period: '2021 - 2022',
          description: t.aveiroDesc,
        },
      ],
    },
  ];

  return (
    <Box sx={{ 
      backgroundColor: "#3050410a",
      display: "flex",
      justifyContent: "center",
      position: "relative",
      padding: { xs: "35px", md: "50px" }
    }}>
      <Box>
        <Typography variant="H38px" align="center" color="textPrimary">
          {t.sectionTitle}
        </Typography>
        <Grid mb={4} mt={1}
          justifySelf={"center"}
          sx={{
            backgroundColor: "#305041",
            width: "120px",
            height: "2px",
            position: "relative",
            overflow: "hidden",
            "::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-120px",
              width: "120px",
              height: "2px",
              background: "linear-gradient(to right, transparent, #fff, transparent)",
              animation: "moveLight 2.5s infinite",
            },
          }}
        >
          <style>
            {`
            @keyframes moveLight {
              0% { left: -120px; }
              100% { left: 120px; }
            }
            `}
          </style>
        </Grid>
        
        {experiences.map((section, index) => (
          <Box key={index} mb={4}>
            <Typography fontWeight={900} fontSize={"20px"}
              color='textThird'
              bgcolor={"#305041"}
              borderRadius={"5px"}
              width={"fit-content"}
              justifySelf={"center"}
              padding={1}
            >
              {section.category}
            </Typography>
            <Timeline
              position={isSmallScreen ? "unset" : "alternate"}
              sx={{
                "&::before": {
                  content: '""',
                  width: isSmallScreen ? "0px" : "unset",
                  position: isSmallScreen ? "absolute" : "unset",
                  left: isSmallScreen ? "calc(50% - 1px)" : "unset",
                },
              }}
            >
              {section.items.map((item, i) => (
                <TimelineItemWithAnimation
                  key={i}
                  item={item}
                  icon={section.category === t.workCategory ? <HomeRepairServiceIcon /> : <SchoolIcon />}
                  isLast={i === section.items.length - 1}
                />
              ))}
            </Timeline>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WorkExperience;
